import React, { useCallback, useEffect, useState } from 'react';

import { TimeInputContainer, TimeInputField } from './styles';

interface TimeInputProps {
  value: string;
  onChange: (time: string) => void;
  minuteStep?: number;
}

export const TimeInput: React.FC<TimeInputProps> = ({ value, onChange, minuteStep = 5 }) => {
  const [inputValue, setInputValue] = useState(value);

  // Sync inputValue with value prop when parent updates
  useEffect(() => {
    setInputValue(value);
  }, [value]);

  const handleBlur = useCallback(() => {
    // Parse the time and preserve seconds
    const timeParts = inputValue.split(':');
    const hours = parseInt(timeParts[0], 10);
    const minutes = parseInt(timeParts[1], 10);
    const seconds = timeParts[2] ? parseInt(timeParts[2], 10) : 0;

    if (isNaN(hours) || isNaN(minutes)) {
      // Invalid input, revert to original value
      setInputValue(value);
      return;
    }

    // Clamp hours to 0-23
    const clampedHours = Math.max(0, Math.min(23, hours));

    // Snap minutes to minuteStep only if minuteStep > 1
    let finalMinutes = minutes;
    if (minuteStep > 1) {
      finalMinutes = Math.round(minutes / minuteStep) * minuteStep;
    }
    const clampedMinutes = Math.max(0, Math.min(59, finalMinutes));

    // Preserve seconds (don't reset to 00)
    const clampedSeconds = Math.max(0, Math.min(59, seconds));

    // Format the time with seconds
    const snappedTime = `${clampedHours.toString().padStart(2, '0')}:${clampedMinutes
      .toString()
      .padStart(2, '0')}:${clampedSeconds.toString().padStart(2, '0')}`;

    setInputValue(snappedTime);
    onChange(snappedTime);
  }, [inputValue, minuteStep, onChange, value]);

  const handleChange = useCallback((e: React.FormEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setInputValue((e.target as HTMLInputElement).value);
  }, []);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      e.stopPropagation();
      if (e.key === 'Enter') {
        e.currentTarget.blur();
      }
    },
    [],
  );

  return (
    <TimeInputContainer>
      <TimeInputField
        type="text"
        value={inputValue}
        onChange={handleChange}
        onBlur={handleBlur}
        onKeyDown={handleKeyDown}
        placeholder="00:00:00"
      />
    </TimeInputContainer>
  );
};
